/*  eslint-disable no-unused-vars */

import $http from '../../shared/http';
import { organisation_preferences } from './organisation_preferences.js';

export const organisations = {
  namespaced: true,
  modules: {
    organisation_preferences: organisation_preferences,
  },
  state: () => {
    return {
      organisationsForSelect: null,
      organisationsForSearchSelect: null,
      organisationRegisterData: null,
      orgSizesForSelect: null,
      organisationData: null,
      organisationEditData: null,
      isOrgEdited: null,
      orgProfilePicture: null,
      churchResourcesExistence: {},
      orgVerificationDocs: [],
    };
  },
  getters: {
    getOrganisationsForSelect: (state) => state.organisationsForSelect,
    getOrganisationsForSearchSelect: (state) => state.organisationsForSearchSelect,
    getOrgSizesForSelect: (state) => (currentLanguage) => {
      const orgSizes = state?.orgSizesForSelect ?? [];
      if (!currentLanguage || !orgSizes.length) {
        return [];
      }

      return (
        orgSizes?.map?.((orgSize) => {
          return {
            value: orgSize.value,
            text: orgSize.localisations?.[currentLanguage]?.name || orgSize.text,
          };
        }) ?? []
      );
    },
    getOrganisationData: (state) => state.organisationData,
    getChurchResourcesExistence: (state) => state.churchResourcesExistence,
    getOrgProfilePictureFormData: (state) => {
      const formData = new FormData();
      if (state.orgProfilePicture) {
        formData.append('org_profile_picture', state.orgProfilePicture);
        return formData;
      } else return null;
    },
    getOrgVerificationDocsFormData: (state) => {
      const formData = new FormData();

      if (state.orgVerificationDocs.length) {
        state.orgVerificationDocs.forEach((doc) => {
          formData.append(doc.key, doc.file);
        });

        return formData;
      } else {
        return null;
      }
    },
    getIsOrgEdited: (state) => state.isOrgEdited,
    getOrgProfilePicture: (state) => state.orgProfilePicture,
    isBranchManagementAllowedForChurch: (state, getters) =>
      getters.getOrganisationData?.denomination?.is_branch_management_allowed ?? false,
  },
  mutations: {
    setOrganisationsForSelect: (state, payload) => (state.organisationsForSelect = payload),
    setOrganisationsForSearchSelect: (state, payload) =>
      (state.organisationsForSearchSelect = payload),
    resetOrganisationsForSearchSelect: (state) => (state.organisationsForSearchSelect = null),
    setOrganisationRegisterData: (state, payload) => (state.organisationRegisterData = payload),
    setOrgSizesForSelect: (state, payload) => (state.orgSizesForSelect = payload),
    setOrganisationData: (state, payload) => (state.organisationData = payload),
    setChurchResourcesExistence: (state, payload) => (state.churchResourcesExistence = payload),
    setIsOrgEdited: (state, payload) => (state.isOrgEdited = payload),
    storeOrgProfilePicture: (state, payload) => (state.orgProfilePicture = payload),
    storeOrgVerificationDocs: (state, payload) => (state.orgVerificationDocs = payload),
  },
  actions: {
    async loadOrganisationsForSelect({ commit }) {
      commit('setOrganisationsForSelect', null);
      return $http('v2')
        .get('organisations/select')
        .then((response) => {
          commit('setOrganisationsForSelect', response.data.data);
          return response;
        });
    },
    async loadOrganisationsForSearchSelect({ commit }, searchQuery) {
      return $http()
        .get('/organisationsForSearchSelect', {
          params: {
            searchQuery: searchQuery,
          },
        })
        .then((response) => {
          commit('setOrganisationsForSearchSelect', response.data.data);
          return response;
        });
    },
    async loadOrgSizesForSelect({ commit }) {
      commit('setOrgSizesForSelect', null);
      return $http('v2')
        .get('/organisation_sizes')
        .then((response) => {
          commit('setOrgSizesForSelect', response.data.data);
          return response;
        });
    },
    async isOrgNameUnique(_ctx, name) {
      return $http()
        .get('/org/isOrgNameUnique', {
          params: {
            org_name: name,
          },
        })
        .then((response) => {
          return response.data.data;
        });
    },
    async isOrgEmailUnique(_ctx, email) {
      return $http()
        .get('/org/isOrgEmailUnique', {
          params: {
            org_email: email,
          },
        })
        .then((response) => {
          return response.data.data;
        });
    },
    async isOrgSlugUnique(_ctx, slug) {
      return $http()
        .get('/org/isOrgSlugUnique', {
          params: {
            org_slug: slug,
          },
        })
        .then((response) => {
          return response.data.data;
        });
    },

    async loadOrganisationData({ commit }, { organisation_id }) {
      if (!organisation_id) {
        throw new Error('Organisation ID not provided');
      }
      return $http('v2')
        .get(`/organisations/${organisation_id}`)
        .then(async (response) => {
          commit('setOrganisationData', response.data.data);
          return response;
        })
        .catch((error) => {
          commit('setOrganisationData', null);
          return Promise.reject(error);
        });
    },

    async LOAD_CHURCH_RESOURCES_EXISTENCE({ commit }, { organisation_id }) {
      if (!organisation_id) {
        throw new Error('Organisation ID not provided');
      }
      return $http('v2')
        .get(`/organisations/${organisation_id}/resources_existence`)
        .then(async (response) => {
          commit('setChurchResourcesExistence', response.data.data);
          return response;
        });
    },

    async editOrganisationAction(ctx, { organisationId, form }) {
      return $http('v2').put(`/organisations/${organisationId}`, form, { notifiable: true });
    },

    async saveOrgProfilePictureAction({ getters }, orgID) {
      return $http('v2', { isMultipartData: true })
        .patch(`/organisations/${orgID}/profile_picture`, getters['getOrgProfilePictureFormData'], {
          notifiable: true,
        })
        .then((response) => {
          return response.data.data;
        });
    },
    async createOrgVerificationDocsAction({ getters }, { id: orgId }) {
      return $http('v2', { isMultipartData: true })
        .post(
          `organisations/${orgId}/verification_document`,
          getters.getOrgVerificationDocsFormData,
          {
            notifiable: true,
          }
        )
        .then((response) => {
          return response.data.data;
        });
    },
    async updateOrgVerificationDocsAction({ getters }, { id: orgId }) {
      return $http('v2', { isMultipartData: true })
        .put(
          `organisations/${orgId}/verification_document`,
          getters.getOrgVerificationDocsFormData,
          {
            notifiable: true,
          }
        )
        .then((response) => {
          return response.data.data;
        });
    },

    async MASS_ASSIGNMENT_TO_DENOMINATION_ACTION(ctx, { ids, denomination_id }) {
      return $http().post(
        '/organisations/denomination-mass-assignment',
        { ids, denomination_id },
        { notifiable: true }
      );
    },
  },
};
